import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const StatusBadge = ({ status }) => {
  const map = {
    paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    created: 'bg-amber-100 text-amber-700 border-amber-200',
    failed: 'bg-rose-100 text-rose-700 border-rose-200',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full border capitalize ${map[status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
      {status}
    </span>
  );
};

const AdminPayments = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/payments/admin/orders`);
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Fetch payments error:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const paidOrders = orders.filter((o) => o.status === 'paid');
  const totalPaidInr = paidOrders.reduce((sum, o) => sum + (Number(o.amount_inr) || 0), 0);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Payment Orders</h1>
        <p className="text-sm text-slate-500 mt-0.5">All Razorpay orders, user-wise. Paid orders activate subscription.</p>
      </div>

      {/* Summary */}
      {orders.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Orders</p>
            <p className="text-2xl font-bold text-slate-900 mt-0.5">{orders.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Paid</p>
            <p className="text-2xl font-bold text-emerald-600 mt-0.5">{paidOrders.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Collected (₹)</p>
            <p className="text-2xl font-bold text-violet-600 mt-0.5">₹{totalPaidInr.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-slate-400 text-sm">No payment orders yet.</p>
            <p className="text-slate-400 text-xs mt-1">Orders appear when users pay via Razorpay on the Subscription page.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Plan</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Order ID</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Payment ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {o.created_at ? new Date(o.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <div className="text-sm font-semibold text-slate-800">{o.user_name || '—'}</div>
                      {o.user_email && <div className="text-xs text-slate-500">{o.user_email}</div>}
                      {o.user_phone && <div className="text-xs text-slate-400">{o.user_phone}</div>}
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-700">{o.plan_name || '—'}</td>
                    <td className="px-5 py-3 text-sm text-right font-semibold text-slate-800">₹{o.amount_inr != null ? Number(o.amount_inr).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '—'}</td>
                    <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
                    <td className="px-5 py-3 text-xs font-mono text-slate-600 max-w-[140px] truncate" title={o.order_id}>{o.order_id || '—'}</td>
                    <td className="px-5 py-3 text-xs font-mono text-slate-600 max-w-[140px] truncate" title={o.razorpay_payment_id || ''}>{o.razorpay_payment_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPayments;
